import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getEmailClassifications } from '../models/EmailModel';
import EmailView from '../views/EmailView';
import { safeDate, formatDate, exportToCSV } from '../utils/helpers';

const EmailController = () => {
    const [data, setData] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [selectedDetailCard, setSelectedDetailCard] = useState(null);
    const [viewMode, setViewMode] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
  // selectedOrderType can be a string (back-compat) or an array of strings for multi-select
  const [selectedOrderType, setSelectedOrderType] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  // subject removed - replaced by order_type (no selectedSubject state)
    const [isExporting, setIsExporting] = useState(false);
    const [refreshCount, setRefreshCount] = useState(0);
    const [isConnectedToLive, setIsConnectedToLive] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
  
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
  
    // Sorting state
    const [sortConfig, setSortConfig] = useState({
      key: 'classification_timestamp',
      direction: 'desc'
    });

    // Real-time clock update
    useEffect(() => {
        const interval = setInterval(() => {
        setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const handleEmailClick = useCallback((email) => {
        setSelectedEmail(email);
    }, []);

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
        exportToCSV(filteredData, `email_classification_${formatDate(new Date(), "YYYY-MM-DD")}.csv`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        } finally {
        setIsExporting(false);
        }
    }, []);

    const connectToLiveEndpoint = useCallback(async () => {
        try {
            setConnectionStatus('connecting');
            setIsRefreshing(true);
        
            const emailData = await getEmailClassifications();
        
            setData(emailData);
            setLastUpdated(new Date());
            setIsConnectedToLive(true);
            setConnectionStatus('connected');
            setRefreshCount(prev => prev + 1);
            } catch (error) {
            console.error('Connection error:', error);
            setConnectionStatus('error');
            setIsConnectedToLive(false);
            } finally {
            setIsLoading(false);
            setIsRefreshing(false);
            }
    }, []);

    useEffect(() => {
        setIsLoading(true);
        connectToLiveEndpoint();
        const interval = setInterval(() => {
        connectToLiveEndpoint();
        }, 300000); // Refresh every 5 minutes

        return () => clearInterval(interval);
    }, [connectToLiveEndpoint]);

    const filteredData = useMemo(() => {
        let filtered = data;
    
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          filtered = filtered.filter((r) => {
            if (r.email_id && r.email_id.toLowerCase().includes(term)) return true;
            if (r.a_number && r.a_number.toLowerCase().includes(term)) return true;
            if (r.subject && r.subject.toLowerCase().includes(term)) return true;
            if (r.client_name && r.client_name.toLowerCase().includes(term)) return true;
            if (r.sender_email && r.sender_email.toLowerCase().includes(term)) return true;
            return false;
          });
        }
    
        if (viewMode === 'monthly' && selectedMonth) {
          filtered = filtered.filter((r) => {
            const dt = safeDate(r.classification_timestamp);
            if (!dt) return false;
            const monthKey = formatDate(dt, "YYYY-MM");
            return monthKey === selectedMonth;
          });
        }
    
        if (selectedPriority) {
          filtered = filtered.filter((r) => r.prediction_priority_value === selectedPriority);
        }
    
        if (selectedCategory) {
          filtered = filtered.filter((r) => r.prediction_category_value === selectedCategory);
        }
    
        if (selectedOrderType && (Array.isArray(selectedOrderType) ? selectedOrderType.length > 0 : String(selectedOrderType).length > 0)) {
          // normalize selected types to an array
          const selectedArr = Array.isArray(selectedOrderType) ? selectedOrderType : [String(selectedOrderType)];
          filtered = filtered.filter((r) => {
            const arr = Array.isArray(r.order_type) && r.order_type.length > 0 ? r.order_type : (r.prediction_order_type_value ? String(r.prediction_order_type_value).split(/[,;]\s*/).map(s => s.trim()).filter(Boolean) : []);
            // match when any selected type is present in the email's order_type array
            return selectedArr.some(sel => arr.includes(sel));
          });
        }
    
        // subject-based filtering removed; use order_type filters instead
    
        if (dateRange.start || dateRange.end) {
          filtered = filtered.filter((r) => {
            const dt = safeDate(r.classification_timestamp);
            if (!dt) return false;
    
            const dateStr = formatDate(dt, "YYYY-MM-DD");
            const inRange = (!dateRange.start || dateStr >= dateRange.start) &&
                           (!dateRange.end || dateStr <= dateRange.end);
            return inRange;
          });
        }
    
        return filtered;
  }, [data, viewMode, selectedMonth, selectedPriority, searchTerm, dateRange, selectedCategory, selectedOrderType]);

      const handleSort = useCallback((key) => {
        setSortConfig(prevConfig => {
          if (prevConfig.key === key) {
            // Toggle direction
            return {
              key,
              direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
            };
          } else {
            // New column, default to ascending
            return {
              key,
              direction: 'asc'
            };
          }
        });
        // Reset to first page when sorting changes
        setCurrentPage(1);
      }, []);

      const getSortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;
    
        return [...filteredData].sort((a, b) => {
          let aVal = a[sortConfig.key];
          let bVal = b[sortConfig.key];
    
          // Handle null/undefined values
          if (aVal == null) aVal = '';
          if (bVal == null) bVal = '';
    
          // Special handling for dates
          if (sortConfig.key === 'classification_timestamp') {
            const aDate = safeDate(aVal);
            const bDate = safeDate(bVal);
            if (!aDate && !bDate) return 0;
            if (!aDate) return 1;
            if (!bDate) return -1;
            return sortConfig.direction === 'asc' 
              ? aDate.getTime() - bDate.getTime()
              : bDate.getTime() - aDate.getTime();
          }
    
          // String comparison
          const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
      }, [filteredData, sortConfig]);

    const totalPages = Math.ceil(getSortedData.length / itemsPerPage);
  
    const getPaginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return getSortedData.slice(startIndex, endIndex);
    }, [getSortedData, currentPage, itemsPerPage]);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    }, []);

    useEffect(() => {
        setCurrentPage(1);
  }, [searchTerm, selectedPriority, selectedCategory, selectedOrderType, dateRange, viewMode, selectedMonth]);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
        }
        if (totalPages === 0 && currentPage !== 1) {
        setCurrentPage(1);
        }
    }, [totalPages, currentPage]);

    const handleManualRefresh = useCallback(() => {
        connectToLiveEndpoint();
    }, [connectToLiveEndpoint]);

    const enhancedMetrics = useMemo(() => {
        const total = filteredData.length;
        const highPriority = filteredData.filter(d => d.prediction_priority_value === "High").length;
        const activeCases = filteredData.filter(d => d.a_number).length;
        const todayCount = filteredData.filter(d => {
          const dt = safeDate(d.classification_timestamp);
          if (!dt) return false;
          const today = new Date();
          return dt.toDateString() === today.toDateString();
        }).length;
    
        return {
          total,
          highPriority,
          activeCases,
          todayCount
        };
      }, [filteredData]);

      const categoryCounts = useMemo(() => {
        const map = new Map();
        filteredData.forEach((r) => {
          const key = r.prediction_category_value || "Unknown";
          map.set(key, (map.get(key) || 0) + 1);
        });
        return Array.from(map.entries()).map(([Category, Count]) => ({ Category, Count }));
      }, [filteredData]);
    
      // subjectCounts removed - subject type deprecated
    
      const priorityCounts = useMemo(() => {
        const map = new Map();
        filteredData.forEach((r) => {
          const key = r.prediction_priority_value || "Normal";
          map.set(key, (map.get(key) || 0) + 1);
        });
        const order = ["High", "Normal", "Low"];
        return Array.from(map.entries())
          .map(([Priority, Count]) => ({ Priority, Count }))
          .sort((a, b) => order.indexOf(a.Priority) - order.indexOf(b.Priority));
      }, [filteredData]);
    
      const orderTypeCounts = useMemo(() => {
        const map = new Map();
        filteredData.forEach((r) => {
          const arr = Array.isArray(r.order_type) && r.order_type.length > 0 ? r.order_type : (r.prediction_order_type_value ? [String(r.prediction_order_type_value)] : []);
          arr.forEach(item => {
            const key = (item || 'Unknown').toString();
            map.set(key, (map.get(key) || 0) + 1);
          });
        });
        return Array.from(map.entries()).map(([OrderType, Count]) => ({ OrderType, Count }));
      }, [filteredData]);
    
      const timeSeries = useMemo(() => {
        const map = new Map();
        filteredData.forEach((r) => {
          const dt = safeDate(r.classification_timestamp);
          let key = "Unknown";
          if (dt) {
            switch (viewMode) {
              case 'hourly':
                key = formatDate(dt, "YYYY-MM-DD HH:mm");
                break;
              case 'daily':
                key = formatDate(dt, "YYYY-MM-DD");
                break;
              case 'monthly':
                key = formatDate(dt, "YYYY-MM");
                break;
              case 'quarterly':
                const year = dt.getFullYear();
                const quarter = Math.floor(dt.getMonth() / 3) + 1;
                key = `${year}-Q${quarter}`;
                break;
              case 'yearly':
                key = formatDate(dt, "YYYY");
                break;
              default:
                key = formatDate(dt, "YYYY-MM-DD");
            }
          }
          map.set(key, (map.get(key) || 0) + 1);
        });
        return Array.from(map.entries())
          .map(([Date, Count]) => ({ Date, Count }))
          .sort((a, b) => (a.Date > b.Date ? 1 : -1));
      }, [filteredData, viewMode]);

  return <EmailView 
        data={data}
        selectedEmail={selectedEmail}
        selectedDetailCard={selectedDetailCard}
        viewMode={viewMode}
        selectedMonth={selectedMonth}
        selectedPriority={selectedPriority}
        selectedCategory={selectedCategory}
  selectedOrderType={selectedOrderType}
        searchTerm={searchTerm}
        currentTime={currentTime}
        dateRange={dateRange}
        isExporting={isExporting}
        refreshCount={refreshCount}
        isConnectedToLive={isConnectedToLive}
        connectionStatus={connectionStatus}
        isMobileMenuOpen={isMobileMenuOpen}
        lastUpdated={lastUpdated}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        sortConfig={sortConfig}
        handleEmailClick={handleEmailClick}
        handleExport={handleExport}
        handleManualRefresh={handleManualRefresh}
        handleSort={handleSort}
        handlePageChange={handlePageChange}
        setSearchTerm={setSearchTerm}
        setSelectedPriority={setSelectedPriority}
        setSelectedCategory={setSelectedCategory}
        setSelectedOrderType={setSelectedOrderType}
  setDateRange={setDateRange}
  handleItemsPerPageChange={handleItemsPerPageChange}
        setSelectedMonth={setSelectedMonth}
        setViewMode={setViewMode}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        setSelectedDetailCard={setSelectedDetailCard}
        setSelectedEmail={setSelectedEmail}
        filteredData={filteredData}
        enhancedMetrics={enhancedMetrics}
  categoryCounts={categoryCounts}
        priorityCounts={priorityCounts}
        orderTypeCounts={orderTypeCounts}
        timeSeries={timeSeries}
        getPaginatedData={getPaginatedData}
        totalPages={totalPages}
        getSortedData={getSortedData}
  />;
}

export default EmailController;